import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { STAGES, stageMeta } from "../lib/stages";
import { StageBadge } from "../components/Stage";
import CsvImportModal from "../components/CsvImportModal";
import { Plus, MagnifyingGlass, Scan, UploadSimple, Warning, Archive } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const PRIORITY_META = {
  urgent: { color: "#FF3B30", label: "URGENT" },
  high: { color: "#FF9500", label: "HIGH" },
  normal: null,
  low: { color: "var(--text-3)", label: "LOW" },
};

export default function ContactsList() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const stage = params.get("stage") || "";
  const eventId = params.get("event_id") || "";
  const priority = params.get("priority") || "";
  const relationship = params.get("relationship_type") || "";
  const showArchived = params.get("archived") === "true";
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", title: "", stage: "new", event_id: "", priority: "normal", relationship_type: "" });
  const [dupes, setDupes] = useState([]);

  const load = () => {
    const args = {};
    if (stage) args.stage = stage;
    if (eventId) args.event_id = eventId;
    if (q) args.q = q;
    if (priority) args.priority = priority;
    if (relationship) args.relationship_type = relationship;
    if (showArchived) args.archived = true;
    api.get("/contacts", { params: args }).then((r) => setItems(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage, eventId, priority, relationship, showArchived]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);

  const eventName = (id) => events.find((e) => e.id === id)?.name;
  const activeEvent = events.find((e) => e.id === eventId);

  const checkForDupes = async (patch) => {
    const base = { ...form, ...patch };
    if (!base.email && !base.phone && !(base.name && base.company)) return;
    try {
      const r = await api.post("/contacts/check-duplicate", {
        email: base.email, phone: base.phone, name: base.name, company: base.company,
      });
      setDupes(r.data.duplicates || []);
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (dupes.length) {
      if (!window.confirm(`There may already be ${dupes.length} contact(s) matching this one. Create a new one anyway?`)) {
        return;
      }
    }
    try {
      const payload = { ...form };
      if (!payload.event_id) delete payload.event_id;
      if (!payload.relationship_type) delete payload.relationship_type;
      await api.post("/contacts", payload);
      setForm({ name: "", email: "", phone: "", company: "", title: "", stage: "new", event_id: "", priority: "normal", relationship_type: "" });
      setDupes([]);
      setShowNew(false);
      toast.success("Contact added");
      load();
    } catch (err) { toast.error("Failed to add"); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="contacts-page">
      <Toaster theme="dark" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// contacts</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Your roster</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/scan" data-testid="contacts-scan-btn" className="btn-secondary flex items-center gap-2"><Scan weight="bold" /> Scan</Link>
          <button onClick={() => setShowImport(true)} data-testid="contacts-import-btn" className="btn-secondary flex items-center gap-2"><UploadSimple weight="bold" /> Import</button>
          <button onClick={() => setShowNew(!showNew)} data-testid="add-contact-btn" className="btn-primary flex items-center gap-2"><Plus weight="bold" /> New</button>
        </div>
      </div>

      <CsvImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => load()}
        events={events}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setParams({})} data-testid="filter-all"
          className={`tag cursor-pointer ${!stage && !eventId && !priority && !relationship && !showArchived ? "text-white" : ""}`}
          style={!stage && !eventId && !priority && !relationship && !showArchived ? { borderColor: "var(--text)" } : {}}>All</button>
        {STAGES.map((s) => (
          <button key={s.id} onClick={() => setParams({ stage: s.id })} data-testid={`filter-${s.id}`}
            className="tag cursor-pointer"
            style={stage === s.id ? { borderColor: s.color, color: s.color } : {}}>
            <span className="stage-dot" style={{ background: s.color }}></span>
            {s.label}
          </button>
        ))}
        <span style={{ borderLeft: "1px solid var(--border)", height: 20, margin: "0 4px" }}></span>
        <select
          value={priority}
          onChange={(e) => {
            const next = Object.fromEntries(params.entries());
            if (e.target.value) next.priority = e.target.value; else delete next.priority;
            setParams(next);
          }}
          className="input-base"
          style={{ padding: "4px 10px", fontSize: 11, width: 'auto', background: "var(--surface-2)", border: "1px solid var(--border)" }}
          data-testid="filter-priority-select"
        >
          <option value="">priority: any</option>
          <option value="urgent">urgent</option>
          <option value="high">high</option>
          <option value="normal">normal</option>
          <option value="low">low</option>
        </select>
        <select
          value={relationship}
          onChange={(e) => {
            const next = Object.fromEntries(params.entries());
            if (e.target.value) next.relationship_type = e.target.value; else delete next.relationship_type;
            setParams(next);
          }}
          className="input-base"
          style={{ padding: "4px 10px", fontSize: 11, width: 'auto', background: "var(--surface-2)", border: "1px solid var(--border)" }}
          data-testid="filter-relationship-select"
        >
          <option value="">type: any</option>
          <option value="prospect">prospect</option>
          <option value="client">client</option>
          <option value="referral_partner">referral partner</option>
          <option value="business_partner">business partner</option>
          <option value="vendor">vendor</option>
          <option value="investor">investor</option>
          <option value="agent">agent</option>
          <option value="broker">broker</option>
          <option value="colleague">colleague</option>
          <option value="friend">friend</option>
        </select>
        <button
          onClick={() => {
            const next = Object.fromEntries(params.entries());
            if (showArchived) delete next.archived; else next.archived = "true";
            setParams(next);
          }}
          className="tag cursor-pointer flex items-center gap-1"
          style={showArchived ? { color: "var(--accent)", borderColor: "var(--accent)" } : {}}
          data-testid="filter-archived"
        >
          <Archive weight="bold" /> {showArchived ? "showing archived" : "archived"}
        </button>
      </div>

      {events.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="overline mr-1">// event:</span>
          {events.slice(0, 8).map((ev) => (
            <button key={ev.id} onClick={() => setParams({ event_id: ev.id })} data-testid={`filter-event-${ev.id}`}
              className="tag cursor-pointer"
              style={eventId === ev.id ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}>
              {ev.name}
            </button>
          ))}
          {eventId && <button onClick={() => setParams({})} className="overline hover:text-white" data-testid="clear-event">clear ×</button>}
        </div>
      )}

      {activeEvent && (
        <div className="border p-4 mb-6" style={{ borderColor: "var(--accent)" }}>
          <div className="overline mb-1" style={{ color: "var(--accent)" }}>// filtering by event</div>
          <div className="font-display font-bold text-xl">{activeEvent.name}</div>
          {activeEvent.location && <div className="overline mt-1">{activeEvent.location}</div>}
        </div>
      )}

      <div className="flex items-center gap-3 border-b mb-6 pb-4" style={{ borderColor: "var(--border)" }}>
        <MagnifyingGlass weight="bold" />
        <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name, company, email..." className="bg-transparent outline-none flex-1 font-mono text-sm" />
        <button onClick={load} className="overline hover:text-white">search</button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="border p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }} data-testid="new-contact-form">
          <div><label className="label-base">Name *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onBlur={() => checkForDupes({})} className="input-base" data-testid="new-name" /></div>
          <div><label className="label-base">Company</label><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} onBlur={() => checkForDupes({})} className="input-base" data-testid="new-company" /></div>
          <div><label className="label-base">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={() => checkForDupes({})} className="input-base" data-testid="new-email" /></div>
          <div><label className="label-base">Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} onBlur={() => checkForDupes({})} className="input-base" data-testid="new-phone" /></div>
          <div><label className="label-base">Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-base" data-testid="new-title" /></div>
          <div>
            <label className="label-base">Stage</label>
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="input-base bg-transparent" data-testid="new-stage">
              {STAGES.map((s) => <option key={s.id} value={s.id} style={{ color: "#000" }}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-base">Priority</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="new-priority">
              <option value="low">low</option>
              <option value="normal">normal</option>
              <option value="high">high</option>
              <option value="urgent">urgent</option>
            </select>
          </div>
          <div>
            <label className="label-base">Relationship type</label>
            <select value={form.relationship_type} onChange={(e) => setForm({ ...form, relationship_type: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="new-relationship-type">
              <option value="">— choose —</option>
              <option value="prospect">prospect</option>
              <option value="client">client</option>
              <option value="referral_partner">referral partner</option>
              <option value="business_partner">business partner</option>
              <option value="vendor">vendor</option>
              <option value="investor">investor</option>
              <option value="agent">agent</option>
              <option value="broker">broker</option>
              <option value="colleague">colleague</option>
              <option value="friend">friend</option>
            </select>
          </div>
          <div>
            <label className="label-base">Event (where you met)</label>
            <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="input-base"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="new-event">
              <option value="">— no event —</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </div>

          {dupes.length > 0 && (
            <div className="md:col-span-2 border p-4" style={{ borderColor: "#FF9500", background: "rgba(255,149,0,0.06)" }} data-testid="duplicate-warning">
              <div className="flex items-center gap-2 mb-2" style={{ color: "#FF9500" }}>
                <Warning weight="fill" /> <b>Possible duplicate{dupes.length > 1 ? "s" : ""} found</b>
              </div>
              <div className="space-y-2 text-sm">
                {dupes.slice(0, 3).map((d) => (
                  <Link key={d.id} to={`/contacts/${d.id}`} className="block hover:underline" data-testid={`dup-link-${d.id}`}>
                    <b>{d.name}</b> {d.email ? `· ${d.email}` : ""} {d.company ? `· ${d.company}` : ""}
                  </Link>
                ))}
              </div>
              <div className="mt-3 text-xs" style={{ color: "var(--text-3)" }}>You can still save this contact — we&apos;ll confirm before creating a duplicate.</div>
            </div>
          )}

          <div className="md:col-span-2 flex gap-3 mt-2">
            <button type="submit" className="btn-primary" data-testid="new-submit">Save contact</button>
            <button type="button" className="btn-secondary" onClick={() => { setShowNew(false); setDupes([]); }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="border" style={{ borderColor: "var(--border)" }}>
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <div className="overline mb-3">// no contacts yet</div>
            <p className="text-[#C7DDE5] mb-6">Scan your first business card or add one manually.</p>
            <Link to="/scan" className="btn-primary inline-flex items-center gap-2"><Scan weight="bold" /> Scan a card</Link>
          </div>
        ) : (
          items.map((c, i) => (
            <Link key={c.id} to={`/contacts/${c.id}`} data-testid={`contact-row-${i}`}
              className={`flex items-center justify-between p-5 hover:bg-[#0F1E24] ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }}>
              <div className="min-w-0">
                <div className="font-semibold text-lg">{c.name}</div>
                <div className="overline mt-1 truncate">{c.title ? `${c.title} · ` : ""}{c.company || c.email || "—"}</div>
              </div>
              <div className="flex items-center gap-4">
                {PRIORITY_META[c.priority] && (
                  <span className="hidden md:inline tag" style={{ color: PRIORITY_META[c.priority].color, borderColor: PRIORITY_META[c.priority].color }} data-testid={`priority-${c.priority}`}>
                    {PRIORITY_META[c.priority].label}
                  </span>
                )}
                {c.relationship_type && (
                  <span className="hidden lg:inline overline" title="Relationship type">{c.relationship_type.replace("_", " ")}</span>
                )}
                {c.event_id && eventName(c.event_id) && (
                  <span className="hidden md:inline tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }} title="Met at">@ {eventName(c.event_id)}</span>
                )}
                <span className="hidden md:inline overline">{new Date(c.created_at).toLocaleDateString()}</span>
                <StageBadge stage={c.stage} />
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
